import React, { useMemo } from 'react';
import { Progress } from 'tea-component';
import RingRatioBubble from '@op/components/Snippets/RingRatioBubble';
import PercentageItem from '@op/components/Snippets/PercentageItem';
import { DBO_V } from '@op/utils/format';
import { parseLinkTime } from '@opm/components/KPICell';
import { IKPIValue } from '../../types';
import './MetricsCell.scss';

interface IMetricsCellProps {
  /** 兼容简单数值（无 DoD） */
  value?: number | null;
  /** 完整 KPI 数据（含 DoD） */
  kpiData?: IKPIValue;
  /** 进度条最大值（list.md `max` 字段，对应字段如 cumulative_impressions / creatives） */
  max?: number;
  /** 数值格式化函数 */
  formatValue?: (v: number) => string | number;
}

/**
 * Table 单元格 KPI 渲染（inline 布局：value + 环比 同行 + 下方进度条）
 *
 * - Impressions 列：value + 环比 + 进度条
 * - Creatives 列：value + 环比 + 进度条
 * - Avg.Days Active 列：value（无环比，无进度条）
 *
 * 实现要点（参考 KPICell）：
 * - link_time 支持 list.md 新结构（4 字段对象）和 detail.md 旧结构（字符串），通过 parseLinkTime 统一
 * - 环比值直接传给 RingRatioBubble（内部 noShow 自动容错）
 * - 进度条用 tea-component <Progress>，max 缺省时不渲染
 */
export default function MetricsCell({
  value,
  kpiData,
  max,
  formatValue = DBO_V.reviews,
}: IMetricsCellProps) {
  const displayValue = kpiData?.value ?? value;
  // 环比百分比：统一用 DBO_V.qoq（= getRationPercentValue），内部已兜底 0/Infinity/负值
  const newValue = kpiData?.value ?? 0;
  const oldValue = kpiData?.old_value ?? 0;
  const ratio = DBO_V.qoq(newValue, oldValue);

  const { oldTime, newTime } = useMemo(
    () => parseLinkTime(kpiData?.link_time),
    [kpiData?.link_time],
  );

  // 进度条归一化比例（0 ~ 100）
  const progressPercent = useMemo(() => {
    if (max == null || max <= 0) return null;
    const numericValue =
      typeof displayValue === 'number' ? displayValue : Number(displayValue);
    if (!Number.isFinite(numericValue) || numericValue <= 0) return 0;
    return Math.min(100, Math.max(0, (numericValue / max) * 100));
  }, [displayValue, max]);

  const formattedValue =
    displayValue == null || displayValue === -1
      ? '-'
      : formatValue(displayValue as number);

  return (
    <div className="dbom-ct-metrics-cell">
      <div className="dbom-ct-metrics-cell__value-row">
        <span className="dbom-ct-metrics-cell__value">{formattedValue}</span>
        <RingRatioBubble
          oldTime={oldTime}
          newTime={newTime}
          oldValue={kpiData?.old_value ?? 0}
          newValue={kpiData?.value ?? 0}
          formateValue={formatValue}
        >
          <PercentageItem percentage={ratio} />
        </RingRatioBubble>
      </div>
      {progressPercent != null && (
        <Progress
          className="dbom-ct-metrics-cell__progress"
          percent={progressPercent}
          strokeWidth={6}
          theme="default"
        />
      )}
    </div>
  );
}
